<script setup lang="ts">
import {
  CalibrationDataDto,
  DeviceReportedCalibrationData,
  instanceOfCalibrationDataDto,
  instanceOfDeviceReportedCalibrationData,
  PtwCloudCalibrationDataDto,
} from '@/api/generator';
import DateTimePicker from '@/components/forms/DateTimePicker/DateTimePicker.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { FormMode } from '@/components/forms/types.ts';
import { lightThemeColors } from '@/styles/vuetify.ts';
import { DateTime } from 'luxon';
import { computed, useTemplateRef } from 'vue';
import { VRow } from 'vuetify/components';
import Button from '@/components/buttons/Button.vue';

type CalibrationInformationProps = {
  calibrationData?:
    | CalibrationDataDto
    | PtwCloudCalibrationDataDto
    | DeviceReportedCalibrationData
    | null;
  colorizeState?: boolean;
  hideCalibratedBy?: boolean;
  hideActivationDate?: boolean;
  hideCalibrationState?: boolean;
  showLastModifiedDate?: boolean;
  showSupportButton?: boolean;
  expanded?: boolean;
};
const mode: FormMode = 'details';

const props = withDefaults(defineProps<CalibrationInformationProps>(), {
  calibrationData: undefined,
  colorizeState: false,
  hideCalibratedBy: false,
  hideActivationDate: false,
  hideCalibrationState: false,
  showLastModifiedDate: false,
  showSupportButton: false,
  expanded: false,
});

type CalibrationInformationEmits = {
  'click:contactSupport': [];
};
defineEmits<CalibrationInformationEmits>();

const calibrationDate = computed(() =>
  props.calibrationData?.calibrationDate
    ? DateTime.fromISO(props.calibrationData?.calibrationDate)
    : undefined,
);
const activationDate = computed(() =>
  props.calibrationData &&
  instanceOfCalibrationDataDto(props.calibrationData) &&
  props.calibrationData?.activationDate
    ? DateTime.fromISO(props.calibrationData?.activationDate)
    : undefined,
);
const stateColor = computed(() => {
  if (props.calibrationData && instanceOfCalibrationDataDto(props.calibrationData)) {
    switch (props.calibrationData?.calibrationState) {
      case 'Active':
        return lightThemeColors.success;
      case 'Outdated':
        return lightThemeColors.warning;
      case 'Invalid':
        return lightThemeColors.error;
      default:
        return lightThemeColors.primary;
    }
  } else {
    return lightThemeColors.primary;
  }
});

// Columns
const calibrationInformationRef = useTemplateRef<InstanceType<typeof VRow>>(
  'calibrationInformationRef',
);
const defaultNumberOfColumns = 6;
const numberOfVisibleColumns = computed<number>(
  () => calibrationInformationRef.value?.$el.querySelectorAll('.v-col, v-spacer').length,
);
const numberOfMissingColumns = computed(() => {
  const missing = defaultNumberOfColumns - numberOfVisibleColumns.value;
  return new Array(missing || 0).fill(0);
});
const cols = computed(() => {
  if (!props.expanded) return undefined;
  const times = 12 / numberOfVisibleColumns.value;
  if (Number.isInteger(times)) {
    return times;
  } else return undefined;
});
</script>

<template>
  <v-row ref="calibrationInformationRef">
    <v-col :cols="cols">
      <TextField
        :label="$t('CalibrationCertificateNo')"
        :mode="mode"
        :modelValue="calibrationData?.calibrationCertificateNumber || undefined"
      />
    </v-col>
    <v-col :cols="cols">
      <DateTimePicker
        :label="$t('CalibrationDate')"
        :mode="mode"
        :modelValue="calibrationDate"
        class="text-body-1"
        formatForDetails="shortDateOnly"
      />
    </v-col>
    <v-col v-if="!hideCalibratedBy" :cols="cols">
      <TextField
        v-if="calibrationData && instanceOfCalibrationDataDto(calibrationData)"
        :label="$t('CalibratedBy')"
        :mode="mode"
        :modelValue="calibrationData?.calibratedBy || undefined"
      />
    </v-col>
    <v-col v-if="!hideActivationDate" :cols="cols">
      <DateTimePicker
        :label="$t('ActivationDate')"
        :mode="mode"
        :modelValue="activationDate"
        class="text-body-1"
        formatForDetails="shortDateOnly"
      />
    </v-col>
    <v-col v-if="!hideCalibrationState" :cols="cols">
      <TextField
        v-if="calibrationData && instanceOfCalibrationDataDto(calibrationData)"
        :label="$t('CalibrationState')"
        :mode="mode"
        :modelValue="calibrationData.calibrationState || undefined"
        :color="colorizeState ? stateColor : undefined"
      />

      <Button
        v-if="
          showSupportButton &&
          calibrationData &&
          instanceOfCalibrationDataDto(calibrationData) &&
          calibrationData.calibrationState === 'Invalid'
        "
        variant="secondary"
        class="mt-8"
        :label="$t('ContactPtwCustomerSupport')"
        @click="$emit('click:contactSupport')"
      />
    </v-col>
    <v-col v-if="showLastModifiedDate" :cols="cols">
      <DateTimePicker
        v-if="
          calibrationData &&
          instanceOfDeviceReportedCalibrationData(calibrationData) &&
          (calibrationData?.lastModificationTime || calibrationData?.creationTime)
        "
        :label="$t('LastReportedDateAndTime')"
        :mode="mode"
        :modelValue="
          (calibrationData.lastModificationTime
            ? DateTime.fromISO(calibrationData.lastModificationTime)
            : undefined) ??
          (calibrationData.creationTime
            ? DateTime.fromISO(calibrationData.creationTime)
            : undefined)
        "
        class="text-body-1"
      />
    </v-col>
    <template v-if="!expanded">
      <VCol v-for="n in numberOfMissingColumns" :key="n" :cols="cols" />
    </template>
  </v-row>
</template>
